import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { queueRefetchInterval } from 'constants/queueRefetchInterval';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  GetAllReportsParams,
  ICancelLabelReport,
  ICheckLabelReportPeriodsParams,
  ICheckLabelReportPeriodsResponse,
  ICreateLabelReport,
  ICreatePeriodReport,
  ICreatePeriodReportForPeriod,
  IGetLabelReportInfiniteParams,
  ILabelPeriodReportsOrderPageResponse,
  ILabelReportPageResponse,
  ILabelReportResponse,
  ILabelReportsByLabelParams,
  ILabelReportsByLabelResponse,
  ILabelReportsByLabelResponseInfinite,
  ILabelReportsOrderPageResponse,
  ILabelReportsOrderQueueResponse,
  ILabelReportsOrderResponse,
  ILabelReportDetailResponse,
  ILabelReportsSearchResponse,
  ILabelReportStatistics,
  IPeriodReportOrderPageResponse,
  LabelReportsOrderResponse,
} from './types';

// Wspólne

export const useGetLabelReportsQueue = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelReportsOrderQueueResponse[], DefaultError>({
    queryKey: ['label-report-queue', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReportOrder/LabelReports/UnderGeneration/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetMerchLabelReportsQueue',
        notFoundPath: '/label',
      });
    },
  });
};

export const useGetLabelReportsStatisticsByLabel = (
  labelId: string,
  params?: ILabelReportsByLabelParams
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelReportStatistics, DefaultError>({
    queryKey: ['reports-by-label-stats', labelId, params],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/Label`,
        params: {
          itemCount: 1,
          page: 0,
          labelId,
          StartSettlementPeriodId: params?.startSettlementPeriodId,
          EndSettlementPeriodId: params?.endSettlementPeriodId,
        },
      });

      const value = await responseHandler<ILabelReportsByLabelResponseInfinite>(
        response,
        {
          groupName: `useGetLabelReportsStatisticsByLabel - ${labelId}`,
          notFoundPath: '/label',
        }
      );

      return value.statistics;
    },
  });
};

export const useGetReportsByLabelInfinite = (
  labelId: string,
  params?: ILabelReportsByLabelParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<ILabelReportsByLabelResponse[], DefaultError>({
    queryKey: ['reports-by-label', labelId, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/Label`,
        params: {
          ...params,
          itemCount,
          page: pageParam,
          labelId,
        },
      });

      const value = await responseHandler<ILabelReportsByLabelResponseInfinite>(
        response,
        {
          groupName: `useGetReportsByLabel - ${labelId}`,
          notFoundPath: '/label',
        }
      );

      return infiniteHandler<ILabelReportsByLabelResponse>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetAllReports = (params: GetAllReportsParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ILabelReportsOrderResponse[], DefaultError>({
    queryKey: ['all-reports', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'LabelReport',
        params: {
          ...params,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<ILabelReportsOrderPageResponse>(
        response,
        {
          groupName: `useGetAllReports - ${pageParam}`,
        }
      );

      return infiniteHandler<ILabelReportsOrderResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useDeleteReport = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteReport');

  return useMutation<{}, DefaultError, string>({
    async mutationFn(id) {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('label-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `LabelReport`,
        body: { labelReportId: id },
      });

      return responseHandler(response, {
        groupName: 'useDeleteReport',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('label-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const useDeleteReportPeriod = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteReportPeriod');

  return useMutation<{}, DefaultError, string>({
    async mutationFn(id) {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('label-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `LabelReport/MassDelete`,
        body: { settlementPeriodId: id },
      });

      return responseHandler(response, {
        groupName: 'useDeleteReportPeriod',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('label-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

// Label Report

export const usePostLabelPeriodReport = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostLabelPeriodReport'
  );

  return useMutation<
    ILabelReportsOrderResponse,
    DefaultError,
    ICreateLabelReport
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('label-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'LabelReportOrder/LabelReport',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostLabelPeriodReport',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('label-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCancelLabelPeriodReport = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useCancelLabelPeriodReport'
  );

  return useMutation<{}, DefaultError, ICancelLabelReport>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.cancel.loading', {
          what: i18next.t('label-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'LabelReportOrder/LabelReport/Cancel',
        body,
      });

      return responseHandler(response, {
        groupName: 'useCancelLabelPeriodReport',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.cancel.success', {
          what: i18next.t('label-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetLabelReportsPeriodOrders = (
  params?: GetAllReportsParams,
  startSettlementPeriodId?: string,
  endSettlementPeriodId?: string
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<ILabelReportsOrderResponse[], DefaultError>({
    queryKey: [
      'label-reports-period-orders',
      params,
      startSettlementPeriodId,
      endSettlementPeriodId,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'LabelReportOrder/LabelReports/Orders',
        params: {
          ...params,
          itemCount,
          page: pageParam,
          ...(startSettlementPeriodId && { startSettlementPeriodId }),
          ...(endSettlementPeriodId && { endSettlementPeriodId }),
        },
      });

      const value = await responseHandler<ILabelPeriodReportsOrderPageResponse>(
        response,
        {
          groupName: `useGetLabelReportsPeriodOrders - ${pageParam}`,
        }
      );

      return infiniteHandler<ILabelReportsOrderResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    refetchInterval: queueRefetchInterval,
  });
};

// PeriodReport

export const usePostPeriodReport = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostPeriodReport');

  return useMutation<
    LabelReportsOrderResponse,
    DefaultError,
    ICreatePeriodReport
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('period-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'LabelReportOrder/LabelReports',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostPeriodReport',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('period-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostPeriodReportForPeriod = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostPeriodReportForPeriod'
  );

  return useMutation<
    LabelReportsOrderResponse,
    DefaultError,
    ICreatePeriodReportForPeriod
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('period-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'LabelReportOrder/LabelReports/ForPeriod',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostPeriodReportForPeriod',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('period-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCancelPeriodReport = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useCancelPeriodReport');

  return useMutation<{}, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-message.cancel.loading', {
          what: i18next.t('period-reports.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'LabelReportOrder/LabelReports/Cancel',
        body: { LabelReportsOrderId: id },
      });

      return responseHandler(response, {
        groupName: 'useCancelPeriodReport',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.cancel.success', {
          what: i18next.t('period-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetPeriodReportsOrders = (params?: GetAllReportsParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<LabelReportsOrderResponse[], DefaultError>({
    queryKey: ['period-reports-orders', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'LabelReportOrder/LabelReports/Orders',
        params: {
          ...params,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<IPeriodReportOrderPageResponse>(
        response,
        {
          groupName: `useGetPeriodReportsOrders - ${pageParam}`,
        }
      );

      return infiniteHandler<LabelReportsOrderResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetLabelReportsInfinite = (
  params: IGetLabelReportInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { Page, ItemCount } = params;
  const { itemCount, page } = getDefaultInfinitePageParam(Page, ItemCount);

  return useInfiniteQuery<ILabelReportResponse[], DefaultError>({
    queryKey: ['label-reports-global', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'LabelReport/Search',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<ILabelReportPageResponse>(response, {
        groupName: `useGetLabelReportsInfinite - ${pageParam}`,
      });

      return infiniteHandler<ILabelReportResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useCheckLabelReportPeriods = (
  params: ICheckLabelReportPeriodsParams
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ICheckLabelReportPeriodsResponse, DefaultError>({
    queryKey: ['check-label-report-periods', params],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'LabelReport/CheckPeriods',
        params,
      });

      return responseHandler(response, {
        groupName: 'useCheckLabelReportPeriods',
      });
    },
  });
};

export const useCheckLabelReportPeriodsMutation = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<
    ICheckLabelReportPeriodsResponse,
    DefaultError,
    ICheckLabelReportPeriodsParams
  >({
    mutationFn: async params => {
      const response = await apiClient({
        method: 'GET',
        url: 'LabelReport/CheckPeriods',
        params,
      });

      return responseHandler(response, {
        groupName: 'useCheckLabelReportPeriods',
      });
    },
  });
};

export const useGetLabelReportsSearch = (
  params: IGetLabelReportInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelReportsSearchResponse, DefaultError>({
    queryKey: ['label-report-search', params],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/Search`,
        params: {
          ...params,
          page: 0,
          itemCount: 1,
        },
      });

      return responseHandler(response, {
        groupName: 'useGetMerchLabelReportsSearch',
      });
    },
  });
};

export const useGetHistoricalReportsByLabelInfinite = (
  labelId: string,
  params?: ILabelReportsByLabelParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<ILabelReportsByLabelResponse[], DefaultError>({
    queryKey: ['historical-reports-by-label', labelId, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/Historical`,
        params: {
          ...params,
          itemCount,
          page: pageParam,
          labelId,
        },
      });

      const value = await responseHandler<ILabelReportsByLabelResponseInfinite>(
        response,
        {
          groupName: `useGetReportsByLabel - ${labelId}`,
          notFoundPath: '/label',
        }
      );

      return infiniteHandler<ILabelReportsByLabelResponse>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetLabelReport = (labelReportId: string, enabled = true) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<ILabelReportDetailResponse, DefaultError>({
    queryKey: ['labelReport', labelReportId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/${labelReportId}`,
      });

      return responseHandler<ILabelReportDetailResponse>(response, {
        groupName: 'useGetLabelReport',
      });
    },
    enabled: !!labelReportId && enabled,
  });
};

export const useGetLabelReportFile = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['labelReport-file', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/File/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetLabelReportFilePreview = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['labelReport-file-preview', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/File/${id}/Preview`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetLabelReportDetailedFile = (
  id: string,
  enabled?: boolean
) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['labelReport-detailed-file', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/DetailedFile/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetLabelReportDetailedFilePreview = (
  id: string,
  enabled?: boolean
) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['labelReport-detailed-file-preview', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/DetailedFile/${id}/Preview`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetLabelReportFiles = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['labelReport-files', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `LabelReport/Files/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGenerateLabelReportFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useGenerateLabelReportFile'
  );

  return useMutation<{}, DefaultError, { labelReportId: string }>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('label-reports.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `LabelReport/GenerateFile`,
        body,
      });

      return responseHandler(response, {
        groupName: 'useGenerateLabelReportFile',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('label-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGenerateLabelReportDetailedFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useGenerateLabelReportDetailedFile'
  );

  return useMutation<{}, DefaultError, { labelReportId: string }>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('label-reports.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `LabelReport/GenerateDetailedFile`,
        body,
      });

      return responseHandler(response, {
        groupName: 'useGenerateLabelReportDetailedFile',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('label-reports.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useSetLabelReportAsUpToDate = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<void, DefaultError, { labelReportId: string }>({
    mutationFn: async body => {
      const response = await apiClient({
        method: 'PUT',
        url: 'LabelReport/SetAsUpToDate',
        body,
      });

      return responseHandler(response, {
        groupName: 'useSetLabelReportAsUpToDate',
      });
    },
  });
};

export const usePasswordProtectLabelReport = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePasswordProtectLabelReport',
  );

  return useMutation<boolean, DefaultError, { labelReportId: string }>({
    mutationFn: async body => {
      loading(i18next.t('service-message.password-protect.loading'));

      const response = await apiClient({
        method: 'POST',
        url: 'LabelReport/PasswordProtect',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePasswordProtectLabelReport',
      });
    },
    onSuccess: () => {
      success(i18next.t('service-message.password-protect.success'));
    },
    onError: dismiss,
  });
};
